import React, { useMemo } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import MuiChip, { ChipProps as MuiChipProps } from '@material-ui/core/Chip';
import { emphasize, fade } from '@material-ui/core/styles/colorManipulator';
import CloseIcon from '@material-ui/icons/Close';
import classnames from 'classnames';
import { isBoolean } from 'lodash';

const styles = ({
  palette: {
    primary,
    common: { white },
    action: { hoverOpacity },
  },
  custom: {
    fontSizes,
    palette: { colorSupportPowder, colorSupportSteel, colorSupportSlate },
  },
}: Theme) =>
  createStyles({
    deletable: {},
    clickable: {},
    sizeSmall: {
      /* Styles applied to the root element if `size="small"`. */
      height: 26,
      fontSize: fontSizes.tiny,
    },
    square: {
      /* Styles applied to the root element if `square=true`. */
      borderRadius: 0,
    },
    /* Styles applied to the root element if `color="supportPowder"`. */
    colorSupportPowder: {
      backgroundColor: colorSupportPowder,
      color: primary.main,
      '&:hover, &:focus': {
        backgroundColor: emphasize(colorSupportPowder, 0.08),
      },
      '&:active': {
        backgroundColor: emphasize(colorSupportPowder, 0.12),
      },
      '& svg': {
        color: colorSupportSteel,
        '&:hover, &:active': {
          color: colorSupportSteel,
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="supportPowder"`. */
    outlinedSupportPowder: {
      backgroundColor: white,
      color: primary.main,
      border: `1px solid ${colorSupportPowder}`,
      '$clickable&:hover, $clickable&:focus, $deletable&:focus': {
        backgroundColor: fade(colorSupportPowder, hoverOpacity),
      },
    },
    /* Styles applied to the root element if `color="supportSlate"`. */
    colorSupportSlate: {
      backgroundColor: colorSupportSlate,
      color: white,
      '&:hover, &:focus': {
        backgroundColor: emphasize(colorSupportSlate, 0.08),
      },
      '&:active': {
        backgroundColor: emphasize(colorSupportSlate, 0.12),
      },
      '& svg': {
        color: white,
        '&:hover, &:active': {
          color: white,
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="supportSlate"`. */
    outlinedSupportSlate: {
      backgroundColor: white,
      color: colorSupportSlate,
      border: `1px solid ${colorSupportSlate}`,
      '$clickable&:hover, $clickable&:focus, $deletable&:focus': {
        backgroundColor: fade(colorSupportSlate, hoverOpacity),
      },
    },
    /* Styles applied to the label element if `dense={true}`. */
    labelDense: {
      paddingLeft: 6,
      paddingRight: 6,
    },
  });

export interface ChipProps extends Omit<MuiChipProps, 'classes' | 'color'>, WithStyles<typeof styles> {
  /**
   * If true or false, the chip will have toggle functionality,
   * switching between default/outlined variants and showing/hiding the delete button
   */
  selected?: boolean;
  /**
   * Adding support for `supportPowder` color
   */
  color?: MuiChipProps['color'] | 'supportPowder' | 'supportSlate';
  size?: 'small';
  square?: boolean;
  dense?: boolean;
}

const Chip: React.FC<ChipProps> = ({
  classes: classesProp,
  className,
  selected,
  color: colorProp,
  variant: variantProp,
  size,
  square,
  dense,
  ...rest
}) => {
  const color = useMemo(() => {
    if (colorProp === 'supportPowder' || colorProp === 'supportSlate') {
      return 'primary';
    }
    return colorProp;
  }, [colorProp]);

  const variant = useMemo(() => {
    if (variantProp) {
      return variantProp;
    }
    if (isBoolean(selected) && !selected) {
      return 'outlined';
    }
    return 'default';
  }, [variantProp, selected]);

  const classes = useMemo(() => {
    const res: MuiChipProps['classes'] = {
      root: classnames(
        {
          [classesProp.sizeSmall]: size === 'small',
          [classesProp.square]: square,
        },
        className,
      ),
      label: classnames({
        [classesProp.labelDense]: dense,
      }),
    };
    if (colorProp === 'supportPowder') {
      res.colorPrimary = classesProp.colorSupportPowder;
      if (variant === 'outlined') {
        res.outlinedPrimary = classesProp.outlinedSupportPowder;
      }
    } else if (colorProp === 'supportSlate') {
      res.colorPrimary = classesProp.colorSupportSlate;
      if (variant === 'outlined') {
        res.outlinedPrimary = classesProp.outlinedSupportSlate;
      }
    }
    return res;
  }, [className, classesProp, colorProp, variant, size, square, dense]);

  return (
    <MuiChip
      classes={classes}
      color={color}
      variant={variant}
      deleteIcon={<CloseIcon fontSize="small" />}
      onDelete={selected ? rest.onClick : undefined}
      {...rest}
    />
  );
};

export default withStyles(styles)(Chip);
